import { DateRangeParameterDTO, GetParkingSessionIdsForParkerDTO, ParkingSessionClient } from './../../../../../base/models/models.swagger.generated';
import { Observable, catchError, forkJoin, map, of, switchMap, tap } from "rxjs";
import { ParkerFunctionExecutor } from "./parker-function-executor.class";
import { ParkerClient, ParkerDTO, UpdateParkerSubscriptionAssignmentDTO } from "src/app/base/models/models.swagger.generated";
import { ReplaceSubscriptionFormGroup } from "../parker-function-replace-subscription-form/parker-function-replace-subscription-form.type";
import { DateTime } from "luxon";
import { UpdateParkerUtil } from "./update-parker.util";
import { ParkerInfo } from '../parker-execution-status';

export class ReplaceSubscriptionExecutor extends ParkerFunctionExecutor {
  private validFrom: DateTime;
  private validTo: DateTime;

  constructor(numTotalRecordsToWorkOn: number,
    private subscriptionReplacementForm: ReplaceSubscriptionFormGroup,
    private parkerClient: ParkerClient,
    private parkingSessionClient: ParkingSessionClient) {
    super(numTotalRecordsToWorkOn);
  }

  protected prepare(): Observable<void> {
    this.validFrom = DateTime.fromJSDate(this.subscriptionReplacementForm.value.validFrom ?? new Date());
    this.validTo = this.subscriptionReplacementForm.value.validTo ? DateTime.fromJSDate(this.subscriptionReplacementForm.value.validTo) : null;
    return of(null); // nothing more to prepare
  }

  protected executeFunctionOnParker(parker: ParkerDTO): Observable<number> {
    const oldSubscriptionId = this.subscriptionReplacementForm.controls.oldSubscriptionId.value;
    const newSubscriptionId = this.subscriptionReplacementForm.controls.newSubscriptionId.value;
    var newSubscriptionExists = false;
    parker.parkerSubscriptionAssignments.forEach(a => {
      if (a.subscription.id === oldSubscriptionId) {
        // if the assignment is active and valid as of now or in the future, then set the end date to the start date of the form
        if (a.active && (!a.validTo || a.validTo >= DateTime.now())) {
          a.validTo = this.validFrom;
        }
      }
      else if (a.subscription.id === newSubscriptionId) {
        // if the assignment is active and valid as of now or in the future, then set the start date and end date of the form
        if (a.active && (!a.validTo || a.validTo >= DateTime.now())) {
          a.validFrom = this.validFrom;
          a.validTo = this.validTo;
          newSubscriptionExists = true;
        }
      }
    });
    const updateParker = UpdateParkerUtil.createUpdateParkerDTO(parker);
    if (!newSubscriptionExists) {
      var subscriptionAssignment = new UpdateParkerSubscriptionAssignmentDTO({
        active: true,
        validFrom: this.validFrom,
        validTo: this.validTo,
        subscriptionId: newSubscriptionId,
        id: 0,
        isFavorite: false
      });
      updateParker.parkerSubscriptionAssignments.push(subscriptionAssignment);
    }
    return this.parkerClient.updateParker(updateParker).pipe(
      switchMap((parker) => {

        if (this.subscriptionReplacementForm.controls.doRecalcSessions.value) {
          return this.recalculateSessions(parker, oldSubscriptionId, newSubscriptionId);
        } else {
          return of(parker.id);
        }
      }));
  }

  public recalculateSessions(parker: ParkerDTO, oldSubscriptionId: number, newSubscriptionId: number): Observable<number> {
    const dto = new GetParkingSessionIdsForParkerDTO({
      clientId: parker.clientId,
      parkerId: parker.id,
      subscriptionId: oldSubscriptionId,
      dateRange: new DateRangeParameterDTO({startDate: (this.validFrom ?? DateTime.now()).toISO(), endDate: (this.validTo ?? DateTime.now()).toISO()})
    });

    return this.parkingSessionClient.getParkingSessionIdsForParker(dto).pipe(
      switchMap((sessionInfos) => {
        const parkerInfo = new ParkerInfo(parker);
        if (sessionInfos.length > 0) {
          return forkJoin([...sessionInfos.map(sessionInfo => this.parkingSessionClient.recalculateParkingSession(sessionInfo.id, newSubscriptionId)
            .pipe(
              tap(() => {
                this.progressInfo.incrementIteration();
              }),
              catchError(error => {
                this.progressInfo.incrementIteration();
                this.executionStatus.push({
                  status: 'error',
                  message: error.message,
                  parkerInfo
                });
                return of(null);
              })
            ))]).pipe(map(() => parker));
        }
        return of(parker);
      }),
      map(parker => parker.id)
    );
  }

  protected preFilterParkersAndUpdateExecutionStatus(parkers: ParkerDTO[]): ParkerDTO[] {
    return parkers;
  }
}
